import * as Sentry from "@sentry/react";
import { Bug, HelpCircle, LifeBuoy, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

export async function openSentryBugForm() {
  try {
    const feedback = Sentry.getFeedback();
    if (!feedback) return;
    const form = await feedback.createForm();
    form.appendToDom();
    form.open();
  } catch {
    // No-op: Sentry feedback integration may be disabled in dev
  }
}

export function FloatingHelpButton() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);

  // The admin portal surfaces "Report a bug" in the sidebar user menu instead,
  // so the floating button doesn't overlap dense data tables / pagination.
  const isAdmin = location.pathname.startsWith("/admin");

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const handleReportBug = () => {
    setOpen(false);
    openSentryBugForm();
  };

  const handleHelpSupport = () => {
    setOpen(false);
    navigate("/support");
  };

  if (isAdmin) return null;

  return (
    <div ref={containerRef} className="fixed bottom-6 right-6 z-40">
      {/* Popover menu */}
      <div
        className={cn(
          "absolute bottom-16 right-0 w-64 bg-white border border-content-border shadow-lg rounded-md overflow-hidden transition-all origin-bottom-right",
          open
            ? "opacity-100 scale-100 pointer-events-auto"
            : "opacity-0 scale-95 pointer-events-none"
        )}
      >
        <div className="px-4 py-3 border-b border-content-border">
          <div className="text-sm font-semibold text-content-foreground">Need help?</div>
          <div className="text-xs text-content-muted mt-0.5">Pick the right channel</div>
        </div>
        <button
          type="button"
          onClick={handleReportBug}
          className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
        >
          <Bug className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-content-foreground">Report a bug</div>
            <div className="text-xs text-content-muted">Something broken or crashing</div>
          </div>
        </button>
        <button
          type="button"
          onClick={handleHelpSupport}
          className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors border-t border-content-border"
        >
          <HelpCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-content-foreground">Help & support</div>
            <div className="text-xs text-content-muted">Questions, feature requests, guides</div>
          </div>
        </button>
      </div>

      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close help menu" : "Open help menu"}
        className={cn(
          "w-12 h-12 rounded-full border border-content-border shadow-[0_6px_16px_rgba(0,0,0,0.18)] flex items-center justify-center transition-colors",
          open
            ? "bg-primary text-primary-foreground hover:bg-primary/90"
            : "bg-white text-primary hover:bg-gray-50"
        )}
      >
        {open ? <X className="w-5 h-5" /> : <LifeBuoy className="w-5 h-5" />}
      </button>
    </div>
  );
}
